import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import {
  Alert,
  Button,
  Dialog,
  Flex,
  Icon,
  Icons,
  Loading,
  Text,
  View,
} from "@phoenix/components";
import {
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@phoenix/components/core/dialog";
import { usePlaygroundStore } from "@phoenix/contexts/PlaygroundContext";
import { prependBasename } from "@phoenix/utils/routingUtils";
import { authFetch } from "../../authFetch";

type OptimizePromptDialogProps = {
  instanceId: number;
  messageId: number;
  originalPrompt: string;
};

// Premium Glassmorphism & Custom Highlight Styles
const loaderContainerCSS = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--global-dimension-size-400) var(--global-dimension-size-200);
  gap: var(--global-dimension-size-200);
`;

const variantListCSS = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  max-height: 400px;
  overflow-y: auto;
  padding: var(--global-dimension-size-100) var(--global-dimension-size-50);
`;

const variantCardCSS = (isSelected: boolean) => css`
  cursor: pointer;
  border-radius: var(--global-dimension-size-100);
  border: 1px solid ${isSelected ? "var(--global-color-status-info-default)" : "var(--global-color-border-default)"};
  background-color: ${isSelected ? "rgba(59, 130, 246, 0.05)" : "var(--global-color-background-surface)"};
  padding: var(--global-dimension-size-200);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: flex-start;
  gap: var(--global-dimension-size-150);

  &:hover {
    border-color: ${isSelected ? "var(--global-color-status-info-default)" : "var(--global-color-border-hover)"};
    background-color: ${isSelected ? "rgba(59, 130, 246, 0.08)" : "var(--global-color-background-hover)"};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  }
`;

const checkmarkContainerCSS = (isSelected: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${isSelected ? "var(--global-color-status-info-default)" : "var(--global-color-border-default)"};
  background-color: ${isSelected ? "var(--global-color-status-info-default)" : "transparent"};
  color: white;
  flex-shrink: 0;
  transition: all 0.25s ease;
`;

export const OptimizePromptDialog = ({
  instanceId,
  messageId,
  originalPrompt,
}: OptimizePromptDialogProps) => {
  const store = usePlaygroundStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [variants, setVariants] = useState<string[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  // Fetch prompt variants from backend optimizer
  useEffect(() => {
    let active = true;
    const fetchVariants = async () => {
      try {
        const response = await authFetch(prependBasename("/api/prompts/optimize"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ prompt: originalPrompt }),
        });

        if (!active) return;

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || `Server returned error status ${response.status}`);
        }

        const data = await response.json();
        if (data && Array.isArray(data.variants)) {
          setVariants(data.variants);
          if (data.variants.length > 0) {
            setSelectedIdx(0); // Default select the first suggestion
          }
        } else {
          throw new Error("Invalid response format received from optimizer API.");
        }
      } catch (err: any) {
        if (active) {
          setError(err.message || "Failed to generate prompt suggestions.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchVariants();
    return () => {
      active = false;
    };
  }, [originalPrompt]);

  const handleApply = useCallback(() => {
    if (selectedIdx === null || !variants[selectedIdx]) return;
    const state = store.getState();
    state.updateMessage({
      instanceId,
      messageId,
      patch: { content: variants[selectedIdx] },
    });
  }, [instanceId, messageId, selectedIdx, variants, store]);

  return (
    <Dialog size="M">
      {({ close }) => (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Optimize Prompt</DialogTitle>
            <DialogCloseButton close={close} />
          </DialogHeader>
          <Flex direction="column" gap="size-200" padding="size-200">
            <Text size="S" color="secondary">
              Below are 5 optimized variations of your prompt generated by **DSPy + Kimi**, designed to preserve the original intent while improving instructions.
            </Text>

            {loading && (
              <div css={loaderContainerCSS}>
                <Loading size="L" />
                <Text size="S" weight="medium">
                  Running DSPy optimization using Kimi LLM...
                </Text>
              </div>
            )}

            {error && (
              <Alert variant="danger" title="Optimization Error">
                {error}
              </Alert>
            )}

            {!loading && !error && (
              <>
                <div css={variantListCSS}>
                  {variants.map((variant, idx) => {
                    const isSelected = selectedIdx === idx;
                    return (
                      <div
                        key={idx}
                        css={variantCardCSS(isSelected)}
                        onClick={() => setSelectedIdx(idx)}
                      >
                        <div css={checkmarkContainerCSS(isSelected)}>
                          {isSelected && (
                            <Icon svg={<Icons.Checkmark />} size="S" color="white" />
                          )}
                        </div>
                        <Flex direction="column" gap="size-50" width="100%">
                          <Text size="XS" weight="bold" color="secondary">
                            Suggestion {idx + 1}
                          </Text>
                          <Text style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                            {variant}
                          </Text>
                        </Flex>
                      </div>
                    );
                  })}
                </div>

                <Flex direction="row" gap="size-150" justifyContent="flex-end" paddingTop="size-100">
                  <Button variant="secondary" size="M" onPress={close}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="M"
                    isDisabled={selectedIdx === null}
                    onPress={() => {
                      handleApply();
                      close();
                    }}
                  >
                    Apply Selected Variant
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </DialogContent>
      )}
    </Dialog>
  );
};
